import type { RouteLocationRaw } from 'vue-router'
import permissions from '../access/permissions.json'
import { applyBackNavigationHooks } from './backNavigationHooks'
import {
  shouldSkipBackNavigation,
  shouldSkipLandingTemplateBackNavigation,
} from './skipBackRoutes'

interface IOptions {
  delta?: number
  fallback?: RouteLocationRaw
  replaceWith?: RouteLocationRaw
  skipBackPatterns?: RegExp[]
  skipBackDelta?: number
  skipBackReplaceFallback?: RouteLocationRaw
}

function isSameHistoryPath(
  firstUrl: string | null | undefined,
  secondUrl: string | null | undefined,
): boolean {
  if (!firstUrl || !secondUrl) {
    return false
  }

  return firstUrl.split('?')[0] === secondUrl.split('?')[0]
}

export function goToBack(options?: IOptions) {
  const router = useRouter()

  if (options?.replaceWith) {
    void router.replace(options.replaceWith)
    return
  }

  const fallbackRoute = options?.fallback ?? permissions.WORKSPACE.ROUTE
  const delta = options?.delta ?? -1
  const backUrl = router.options.history.state.back as string | null
  const currentUrl = router.options.history.state.current as string | null
  const hasHistory = backUrl !== null
  const backNavigationContext = { backUrl, currentUrl }
  const { forceFallback, fallbackRoute: resolvedFallbackRoute } = applyBackNavigationHooks(
    backNavigationContext,
    fallbackRoute,
  )
  const isSkippedTemplateBackOnLanding = shouldSkipLandingTemplateBackNavigation(
    backUrl,
    currentUrl,
    options?.skipBackPatterns,
  )
  const isSkippedBack = shouldSkipBackNavigation(backUrl, options?.skipBackPatterns)

  if (isSkippedTemplateBackOnLanding && options?.skipBackReplaceFallback) {
    void router.replace(options.skipBackReplaceFallback)
    return
  }

  if (isSkippedTemplateBackOnLanding && options?.skipBackDelta !== undefined && hasHistory) {
    router.go(options.skipBackDelta)
    return
  }

  const shouldUseFallback = !hasHistory
    || isSkippedTemplateBackOnLanding
    || isSkippedBack
    || forceFallback

  if (!shouldUseFallback && isSameHistoryPath(backUrl, currentUrl)) {
    router.go(delta * 2)
    return
  }

  if (!shouldUseFallback) {
    router.go(delta)
    return
  }

  if (resolvedFallbackRoute) {
    router.replace(resolvedFallbackRoute)
  }
}
